/**
 * Código GFIP e Enquadramento da Previdência Social: a decisão de preencher e a de avisar (#302).
 *
 * O servidor calcula o código pelos riscos do cargo; aqui só se decide, sem tocar no formulário,
 * se o cálculo pode escrever em cada campo e quando o que está na tela diverge dele. Funções
 * puras: a página aplica o resultado e guarda as origens fora do estado do react-hook-form.
 */

import type { AposentadoriaEspecialDoCargo } from '../../services/efetivosService';
import {
  ENQUADRAMENTO_POR_CODIGO,
  codigoDoEnquadramento,
  type CodigoAposentadoriaEspecial,
  type SocialSecurityClassification,
} from '../../types/employee';

export type CampoAposentadoria = 'gfip' | 'enquadramento';

/**
 * Quem pôs o valor no campo. Só `sistema` acompanha um novo cálculo; `salvo` (veio do
 * funcionário carregado) e `pessoa` (digitou ou apagou) nunca são sobrescritos. Campo vazio que
 * ninguém tocou não tem origem.
 */
export type OrigemDoCampo = 'sistema' | 'salvo' | 'pessoa';

export type OrigensDosCampos = Partial<Record<CampoAposentadoria, OrigemDoCampo>>;

export interface ValoresDosCampos {
  gfip: string;
  enquadramento: SocialSecurityClassification | '';
}

export interface EstadoDosCampos {
  valores: ValoresDosCampos;
  origens: OrigensDosCampos;
}

export type EventoDoCargo =
  | { tipo: 'resposta'; resposta: AposentadoriaEspecialDoCargo }
  | { tipo: 'cargo-limpo' }
  /** O cargo trocou e a resposta dele não chegou (calculando, sem acesso ou falha). */
  | { tipo: 'sem-resposta' };

function estaVazio(valor: string): boolean {
  return valor.trim() === '';
}

/**
 * O que acontece com os dois campos quando o cargo responde ou deixa de existir. Cada campo
 * decide por conta própria, pela sua origem (D3):
 *
 * - cálculo com código: preenche o campo vazio sem origem e acompanha o que o sistema pôs;
 * - cálculo sem código, ou cargo limpo (D8): o que o sistema pôs vinha de um cargo que já não
 *   vale e volta a vazio;
 * - em qualquer caso, `salvo` e `pessoa` ficam como estão.
 */
export function decidirPreenchimento(estado: EstadoDosCampos, evento: EventoDoCargo): EstadoDosCampos {
  const codigo = evento.tipo === 'resposta' ? evento.resposta.codigo : null;
  const valores: ValoresDosCampos = { ...estado.valores };
  const origens: OrigensDosCampos = { ...estado.origens };

  const acompanhaOCalculo = (campo: CampoAposentadoria): boolean => {
    const origem = origens[campo];
    if (origem === 'salvo' || origem === 'pessoa') return false;
    return origem === 'sistema' || estaVazio(valores[campo]);
  };

  if (codigo !== null) {
    if (acompanhaOCalculo('gfip')) {
      valores.gfip = String(codigo);
      origens.gfip = 'sistema';
    }
    if (acompanhaOCalculo('enquadramento')) {
      valores.enquadramento = ENQUADRAMENTO_POR_CODIGO[codigo];
      origens.enquadramento = 'sistema';
    }
  } else {
    if (origens.gfip === 'sistema') {
      valores.gfip = '';
      delete origens.gfip;
    }
    if (origens.enquadramento === 'sistema') {
      valores.enquadramento = '';
      delete origens.enquadramento;
    }
  }

  return { valores, origens };
}

/**
 * Os campos cujo valor não bate com o código calculado. O GFIP compara só os dígitos depois de
 * aparar espaços ("4 " vale 4; "004" e "4-" divergem); o Enquadramento compara o código do enum.
 * Campo em branco diverge quando há código. Sem código calculado nada diverge.
 */
export function divergenciasDoCodigo(
  codigo: CodigoAposentadoriaEspecial | null,
  gfip: string,
  enquadramento: SocialSecurityClassification | ''
): CampoAposentadoria[] {
  if (codigo === null) return [];

  const divergentes: CampoAposentadoria[] = [];
  if (gfip.trim() !== String(codigo)) divergentes.push('gfip');
  if (codigoDoEnquadramento(enquadramento) !== codigo) divergentes.push('enquadramento');
  return divergentes;
}

/**
 * Identifica a divergência que a pessoa decidiu manter. Qualquer mudança de valor ou de código
 * muda a chave, e "Manter como está" deixa de valer sozinho.
 */
export function chaveDoAviso(
  codigo: CodigoAposentadoriaEspecial | null,
  gfip: string,
  enquadramento: SocialSecurityClassification | ''
): string {
  return `${codigo ?? ''}|${gfip}|${enquadramento}`;
}

export interface AvisoDeDivergencia {
  /** Campos que não batem com o código calculado. */
  divergentes: CampoAposentadoria[];
  /** A pessoa clicou em "Manter como está" para exatamente esta divergência. */
  mantido: boolean;
  /** Há divergência e ninguém decidiu: a faixa pede decisão e o botão final "Salvar" (os campos estão na última etapa) perde o preenchimento. */
  aberta: boolean;
}

export function avisoDeDivergencia(
  codigo: CodigoAposentadoriaEspecial | null,
  gfip: string,
  enquadramento: SocialSecurityClassification | '',
  mantidoPara: string | null
): AvisoDeDivergencia {
  const divergentes = divergenciasDoCodigo(codigo, gfip, enquadramento);
  const mantido = divergentes.length > 0 && mantidoPara === chaveDoAviso(codigo, gfip, enquadramento);
  return { divergentes, mantido, aberta: divergentes.length > 0 && !mantido };
}
